import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import StatusBadge from './StatusBadge';

const ProductDetailsModal = ({ batch, onClose, onRetryProduct }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Usar detalle real si existe; si no existe, intentar reconstruir fallidos desde errorSummary.
  const realProducts = useMemo(() => {
    if (Array.isArray(batch?.products) && batch.products.length) return batch.products;
    if (Array.isArray(batch?.details?.products) && batch.details.products.length) return batch.details.products;

    const summary = Array.isArray(batch?.errorSummary) ? batch.errorSummary : [];
    if (summary.length) {
      return summary.map((line, idx) => {
        const raw = String(line || '');
        const parts = raw.split(':');
        const title = parts.length > 1 ? parts.slice(0, -1).join(':').trim() : '';
        const error = parts.length > 1 ? parts[parts.length - 1].trim() : raw;
        return {
          id: `summary_${idx}`,
          sku: '',
          title: title || 'Producto',
          price: 0,
          status: 'failed',
          mlId: '',
          mlUrl: '',
          error: error || raw,
          publishedAt: batch?.date || batch?.addedAt || ''
        };
      });
    }

    // Último fallback: lista vacía (no hay detalle guardado)
    return [];
  }, [batch]);

  const filteredProducts = realProducts?.filter(product => {
    const matchesSearch = product?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.sku?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = !statusFilter || product?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    })?.format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const headers = ['SKU', 'Título', 'Precio', 'Estado', 'ID MercadoLibre', 'Error', 'Fecha Publicación'];
    const csvContent = [
      headers?.join(','),
      ...filteredProducts?.map(product => [
        product?.sku,
        `"${product?.title}"`,
        product?.price,
        product?.status,
        product?.mlId || '',
        `"${product?.error || ''}"`,
        product?.publishedAt || ''
      ]?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `productos_${batch?.fileName}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body.appendChild(a);
    a?.click();
    document.body.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1050 p-4">
      <div className="bg-surface rounded-lg shadow-elevated w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Detalles del Lote</h2>
            <p className="text-text-secondary mt-1">{batch?.fileName} - {batch?.totalProducts} productos</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              iconName="Download"
              iconPosition="left"
            >
              Exportar CSV
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-border">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Buscar por título o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
              />
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Todos los estados</option>
                <option value="success">Exitosos</option>
                <option value="failed">Fallidos</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {/* Desktop Table */}
          <div className="hidden lg:block">
            <table className="w-full">
              <thead className="bg-muted/30 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    MercadoLibre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredProducts?.map((product) => (
                  <tr key={product?.id} className="hover:bg-muted/20">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-text-primary truncate max-w-xs">
                          {product?.title}
                        </p>
                        {product?.error && (
                          <p className="text-xs text-error mt-1 flex items-center">
                            <Icon name="AlertCircle" size={12} className="mr-1" />
                            {product?.error}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                      {product?.sku}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                      {formatPrice(product?.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={product?.status} size="sm" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {product?.mlUrl ? (
                        <div>
                          <a
                            href={product?.mlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 flex items-center"
                          >
                            <Icon name="ExternalLink" size={12} className="mr-1" />
                            {product?.mlId}
                          </a>
                          <p className="text-xs text-text-secondary mt-1">
                            {formatDate(product?.publishedAt)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-text-secondary">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {product?.status === 'failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRetryProduct(product?.id)}
                          iconName="RotateCcw"
                        >
                          Reintentar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden p-4 space-y-4">
            {filteredProducts?.map((product) => (
              <div key={product?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-text-primary truncate">
                      {product?.title}
                    </h3>
                    <p className="text-xs text-text-secondary mt-1">SKU: {product?.sku}</p>
                  </div>
                  <StatusBadge status={product?.status} size="sm" />
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <span className="text-text-secondary">Precio:</span>
                    <p className="font-medium text-text-primary">{formatPrice(product?.price)}</p>
                  </div>
                  {product?.mlId && (
                    <div>
                      <span className="text-text-secondary">ML ID:</span>
                      <a
                        href={product?.mlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 flex items-center"
                      >
                        <Icon name="ExternalLink" size={12} className="mr-1" />
                        {product?.mlId}
                      </a>
                    </div>
                  )}
                </div>

                {product?.error && (
                  <div className="mb-3">
                    <p className="text-xs text-error flex items-center">
                      <Icon name="AlertCircle" size={12} className="mr-1" />
                      {product?.error}
                    </p>
                  </div>
                )}

                {product?.status === 'failed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRetryProduct(product?.id)}
                    iconName="RotateCcw"
                    fullWidth
                  >
                    Reintentar Producto
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/20">
          <div className="flex items-center justify-between text-sm text-text-secondary">
            <span>Mostrando {filteredProducts?.length} producto(s)</span>
            <span>Última actualización: {formatDate(batch?.date)}</span>
          </div>
          {realProducts?.length === 0 && (
            <div className="mt-2 text-xs text-text-secondary">
              Este lote no tiene detalle guardado por producto. A partir de ahora se guardará automáticamente durante la publicación.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;